import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@powersync/react";
import { PMAvatar, PMCheckbox, PMField, PMFormActions, PMIcon } from "@/ds/vendor";
import { useApp } from "@/app/AppContext";
import { useLocations, usePeople, useVisits } from "@/data/queries";
import { useMutations } from "@/data/mutations";
import { levelDef } from "@/lib/levels";
import { Modal } from "@/ui/Modal";
import { avatarColor, fullName, shortDate } from "@/ui/format";
import { addDays, TYPES } from "./taskModel";

// New task. Everything a field advisor sets when delegating work:
// what · what kind · where (any level of the team's hierarchy) · when ·
// who · which visit it came out of. Opens the created task afterwards.
export interface ComposerSeed { locationId?: string; visitId?: string; title?: string }

export function TaskComposer({ open, onClose, onCreated, seed }: { open: boolean; onClose: () => void; onCreated: (id: string) => void; seed?: ComposerSeed }) {
  const { hierarchy, userId, mode } = useApp();
  const m = useMutations();
  const people = usePeople().data;
  const all = useLocations().data;
  const links = useQuery<{ zone_id: string; member_id: string }>("SELECT zone_id, member_id FROM mobile_zone_memberships").data;
  const top = hierarchy.levels[0];
  const child = hierarchy.levels[1];

  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [type, setType] = useState<0 | 1 | 2>(0);
  const [parentId, setParentId] = useState("");
  const [childId, setChildId] = useState("");
  const [date, setDate] = useState(addDays(1));
  const [time, setTime] = useState("");
  const [assignees, setAssignees] = useState<string[]>([userId]);
  const [visitId, setVisitId] = useState("");
  const [another, setAnother] = useState(false);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);

  // Seed from context (a location page, a visit) each time the composer opens.
  useEffect(() => {
    if (!open) return;
    setTouched(false);
    setTitle(seed?.title ?? "");
    setVisitId(seed?.visitId ?? "");
    const loc = all.find((l) => l.id === seed?.locationId);
    if (loc) {
      const parent = links.find((z) => z.member_id === loc.id)?.zone_id;
      if (loc.level === top.key || !parent) { setParentId(loc.id); setChildId(""); } else { setParentId(parent); setChildId(loc.id); }
    }
    setTimeout(() => titleRef.current?.focus(), 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const parents = all.filter((l) => (l.level || top.key) === top.key);
  const children = useMemo(() => {
    const ids = new Set(links.filter((z) => z.zone_id === parentId).map((z) => z.member_id));
    return all.filter((l) => ids.has(l.id));
  }, [links, all, parentId]);
  const locationId = childId || parentId;
  const scope = useMemo(() => (parentId ? [parentId, ...children.map((c) => c.id)] : []), [parentId, children]);
  const visits = useVisits({ locationIds: scope, limit: 12 }).data;

  const quick = [
    { label: "Today", v: addDays(0) }, { label: "Tomorrow", v: addDays(1) }, { label: "In 3 days", v: addDays(3) }, { label: "Next week", v: addDays(7) },
  ];
  const toggle = (id: string) => setAssignees((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));
  const errors = { title: !title.trim() ? "Title is required" : "", location: !locationId ? `Choose a ${top.label.toLowerCase()}` : "", time: type === 2 && !time ? "Appointments need a time" : "", who: !assignees.length ? "Assign at least one person" : "" };
  const valid = !Object.values(errors).some(Boolean);
  const othersInLive = mode === "live" && assignees.some((a) => a !== userId);

  const reset = () => { setTitle(""); setNotes(""); setTime(""); setVisitId(""); setTouched(false); };
  const submit = async () => {
    setTouched(true);
    if (!valid || busy) return;
    setBusy(true);
    const id = await m.createReminder({ locationId, title, notes, date, time: time || undefined, type, visitId: visitId || null, assigneeIds: assignees });
    setBusy(false);
    if (!id) return;
    if (another) { reset(); titleRef.current?.focus(); return; }
    reset();
    onCreated(id);
  };

  return (
    <Modal open={open} onClose={onClose} title="New task" size="lg"
      footer={
        <div className="composer-foot">
          <label className="composer-another"><PMCheckbox checked={another} onChange={() => setAnother((a) => !a)} label="Create another" /></label>
          <PMFormActions onCancel={onClose} onConfirm={submit} confirmLabel={busy ? "Creating…" : "Create task"} confirmDisabled={busy} />
        </div>
      }>
      <form className="composer" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        <div className="composer-main">
          <input ref={titleRef} className="composer-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What needs doing?" aria-label="Task title" aria-invalid={touched && !!errors.title} />
          {touched && errors.title && <span className="composer-err">{errors.title}</span>}
          <textarea className="composer-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add details, rates, what to check, who to call…" rows={5} aria-label="Notes" />
          <div className="composer-types" role="radiogroup" aria-label="Task type">
            {TYPES.map((t) => (
              <button type="button" key={t.value} role="radio" aria-checked={type === t.value} className={`composer-type ${t.tone} ${type === t.value ? "is-on" : ""}`} onClick={() => setType(t.value)}>
                <PMIcon name={t.icon} size={18} /> {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="composer-side">
          <section>
            <h4>Where</h4>
            <PMField label={top.label} type="select" value={parentId} onChange={(e: any) => { setParentId(e.target.value); setChildId(""); setVisitId(""); }}
              options={[{ value: "", label: `Choose a ${top.label.toLowerCase()}` }, ...parents.map((l) => ({ value: l.id, label: `${levelDef(l.level, hierarchy.key).emoji} ${l.label}` }))]}
              error={touched ? errors.location : undefined} />
            {child && parentId && children.length > 0 && (
              <PMField label={child.label} type="select" value={childId} onChange={(e: any) => setChildId(e.target.value)} optional
                options={[{ value: "", label: `Whole ${top.label.toLowerCase()}` }, ...children.map((l) => ({ value: l.id, label: `${child.emoji} ${l.label}` }))]} />
            )}
          </section>

          <section>
            <h4>When</h4>
            <div className="composer-quick">
              {quick.map((q) => <button type="button" key={q.label} className={date === q.v ? "is-on" : ""} onClick={() => setDate(q.v)}>{q.label}</button>)}
            </div>
            <div className="composer-row">
              <PMField label="Date" type="date" value={date} onChange={(e: any) => setDate(e.target.value)} />
              <PMField label="Time" type="time" value={time} onChange={(e: any) => setTime(e.target.value)} optional={type !== 2} error={touched ? errors.time : undefined} />
            </div>
          </section>

          <section>
            <h4>Assign to</h4>
            <div className="composer-people">
              {people.map((p) => {
                const on = assignees.includes(p.user_id);
                return (
                  <button type="button" key={p.user_id} className={`composer-person ${on ? "is-on" : ""}`} aria-pressed={on} onClick={() => toggle(p.user_id)}>
                    <PMAvatar name={fullName(p)} size={26} color={avatarColor(p.user_id)} />
                    <span>{p.user_id === userId ? "Me" : p.name}</span>
                    {on && <PMIcon name="check" size={14} />}
                  </button>
                );
              })}
            </div>
            {touched && errors.who && <span className="composer-err">{errors.who}</span>}
            {othersInLive && <p className="composer-note">Teammates are notified once the web API ships. Until then the task is assigned to you.</p>}
          </section>

          {visits.length > 0 && (
            <section>
              <h4>From a visit</h4>
              <PMField label="Linked visit" type="select" value={visitId} onChange={(e: any) => setVisitId(e.target.value)} optional
                options={[{ value: "", label: "Not linked" }, ...visits.map((v) => ({ value: v.id, label: `${v.location_label} · ${shortDate(v.started_at)}` }))]} />
            </section>
          )}
        </div>
      </form>
    </Modal>
  );
}
